import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { UserKeys, getAllGroupTemplates } from '../../../api/users-api';
import { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { GroupTemplate } from '../../../../types';
import DeleteGroupTemplateDialog from './delete-group-template-dialog';
import RenameGroupTemplateDialog from './rename-group-template-dialog';
import CreateTemplateDialog from './create-template/create-template-dialog';
import EditTemplateDialog from './edit-template/edit-template-dialog';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { GroupTemplatesGridToolbar } from './group-templates-grid-toolbar';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { pagesConfig } from '../../../shared/pages-config';

import Tooltip from '@mui/material/Tooltip';
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline';
import Edit from '@mui/icons-material/Edit';
import Feed from '@mui/icons-material/Feed';

export default function GroupTemplatesGrid() {
  const { directoryRoles, changeLogRoles, features } = useUserSettings();
  const isGroupsGovernanceEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );

  const navigate = useNavigate();

  const [selectedTemplate, setSelectedTemplate] = useState<GroupTemplate>();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openDeleteTemplates, setOpenDeleteTemplates] = useState(false);
  const [openCreateTemplate, setOpenCreateTemplate] = useState(false);
  const [openEditTemplate, setOpenEditTemplate] = useState(false);
  const [openRenameTemplate, setOpenRenameTemplate] = useState(false);

  const {
    data: templates,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [UserKeys.GroupTemplates],
    queryFn: getAllGroupTemplates,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group templates'),
    },
  });

  const selectedTemplates =
    templates?.filter(
      (template) =>
        template.keyString && selectedRows.includes(template.keyString)
    ) || [];
  const columns: GridColDef<GroupTemplate>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'groupTemplateName',
        headerName: 'Template Name',
        flex: 1,
      },
      {
        field: 'groupTemplateDesc',
        headerName: 'Description',
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditGroupTemplates.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Edit Template"
                icon={
                  <Tooltip arrow title="Edit">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  if (isGroupsGovernanceEnabled) {
                    navigate(
                      `/${pagesConfig.groupTemplates.link}/${generatePath(
                        pagesConfig.editGroupTemplate.link,
                        { id: String(params.row.id) }
                      )}`
                    );
                    return;
                  }
                  setSelectedTemplate(params.row);
                  setOpenEditTemplate(true);
                }}
                showInMenu
              />
            );
            buttonArray.push(
              <GridActionsCellItem
                label="Rename"
                icon={
                  <Tooltip arrow title="Rename">
                    <DriveFileRenameOutline />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedTemplate(params.row);
                  setOpenRenameTemplate(true);
                }}
                showInMenu
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [directoryRoles, isGroupsGovernanceEnabled, navigate]
  );

  const { viewType } = useUserSettings();
  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      directoryRoles,
      setOpenCreateTemplate,
      setOpenDeleteTemplates,
      selectedRows,
    }),
    [directoryRoles, selectedRows]
  );
  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(GroupTemplatesGridToolbar, toolbarProps),
          ...createGridOverlays('Group Templates', <Feed />),
        }}
        defaultGridState={defaultGridState}
        gridKey={viewType.groupTemplatesGridStates?.activeGridState}
        checkboxSelection
        disableRowSelectionOnClick
        getRowId={(row) => row.keyString ?? ''}
        rows={templates || []}
        columns={columns}
        loading={isLoading || isFetching}
        gridStateKey="groupTemplatesGridStates"
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
      {selectedRows && openDeleteTemplates ? (
        <DeleteGroupTemplateDialog
          open={openDeleteTemplates}
          handleClose={() => setOpenDeleteTemplates(false)}
          selectedRows={selectedTemplates}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedTemplate ? (
        <RenameGroupTemplateDialog
          open={openRenameTemplate}
          handleClose={() => setOpenRenameTemplate(false)}
          selectedTemplate={selectedTemplate}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {!isGroupsGovernanceEnabled && (
        <CreateTemplateDialog
          open={openCreateTemplate}
          handleClose={() => setOpenCreateTemplate(false)}
          changeLogRoles={changeLogRoles}
        />
      )}
      {!isGroupsGovernanceEnabled && selectedTemplate ? (
        <EditTemplateDialog
          open={openEditTemplate}
          handleClose={() => setOpenEditTemplate(false)}
          selectedTemplate={selectedTemplate}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
